/**
 * Ecriture des nombres, lecture des reponses, et correction.
 *
 * Ici, le Quebec: la VIRGULE decimale est la forme normale (« 0,5 »). Refuser
 * « 0.5 » serait aussi absurde que refuser « 0,5 » — on accepte les deux, ainsi
 * que l'unite collee ou non (« 20 cm », « 20cm », « 20 »).
 *
 * Ce qu'on n'ecrase PAS: les accents. « a » et « à » sont exactement le sujet
 * de l'exercice sur les homophones; les confondre viderait la moitie du module
 * de son sens.
 */
import type { Question } from './types.ts'

/** Pourcentages de reussite a atteindre pour une, deux, puis trois etoiles. */
export const SEUILS_ETOILES: readonly number[] = [50, 70, 90]

/**
 * Ecrit un nombre a la francaise: virgule decimale, pas de zeros inutiles.
 * `0.30000000000000004` (le bonjour des flottants) redevient « 0,3 ».
 */
export function formaterNombre(v: number, decimales = 2): string {
  const arrondi = Number(v.toFixed(decimales))
  return String(arrondi).replace('.', ',')
}

/**
 * Met une chaine sous une forme comparable: espaces normalises, apostrophes
 * typographiques ramenees a l'apostrophe droite, minuscules. Les accents
 * restent.
 */
export function normaliser(s: string): string {
  return s
    .replace(/[\u2018\u2019\u02bc]/g, "'")
    .replace(/[\u00a0\u202f\u2009\u2007]/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
}

/**
 * Valeur numerique d'une reponse ecrite, ou `null` si ce n'en est pas une.
 * Comprend « 0,5 », « 0.5 », « 20 cm », « 78,5 cm² », « 3/4 », « 25 % ».
 */
export function valeurNumerique(s: string): number | null {
  const t = normaliser(s)
    .replace(/\s/g, '')
    .replace(/[\u2212\u2013\u2014]/g, '-')
    .replace(/,/g, '.')
  // L'unite est collee a la fin: on la retire, elle est deja dans la question.
  const noyau = t.replace(/[^0-9./-]+$/u, '')
  if (noyau === '') return null
  const fraction = /^(-?\d+)\/(\d+)$/.exec(noyau)
  if (fraction) {
    const bas = Number(fraction[2])
    return bas === 0 ? null : Number(fraction[1]) / bas
  }
  if (!/^-?\d+(\.\d+)?$/.test(noyau)) return null
  const v = Number(noyau)
  return Number.isFinite(v) ? v : null
}

/** Tolerance de comparaison: deux centiemes de rien, pour les flottants. */
const EPSILON = 1e-9

/**
 * Vrai si la reponse donnee vaut celle attendue.
 *
 * Deux chemins, dans cet ordre: la comparaison textuelle (c'est elle qui tranche
 * pour le francais, ou « à » n'est pas « a ») puis, seulement pour les questions
 * numeriques, la comparaison de valeurs (c'est elle qui accepte la virgule, le
 * point, l'unite et la fraction equivalente).
 */
export function corriger(q: Question, donnee: string): boolean {
  const d = normaliser(donnee)
  if (d === '') return false
  for (const r of q.reponses) if (normaliser(r) === d) return true
  if (q.saisie !== 'nombre') return false
  const a = valeurNumerique(donnee)
  if (a === null) return false
  for (const r of q.reponses) {
    const b = valeurNumerique(r)
    if (b !== null && Math.abs(a - b) < EPSILON) return true
  }
  return false
}

/** Les unites qui se collent au nombre: « 47° », jamais « 47 ° ». */
const COLLEES = new Set(['\u00b0', '%'])

/** La reponse qu'on montre a l'enfant quand il s'est trompe. */
export function reponseAffichee(q: Question): string {
  const premiere = q.reponses[0] ?? ''
  if (!q.unite) return premiere
  return COLLEES.has(q.unite) ? `${premiere}${q.unite}` : `${premiere} ${q.unite}`
}

/** Nombre d'etoiles (0 a 3) pour un pourcentage de reussite. */
export function etoiles(pourcentage: number): number {
  let n = 0
  for (const seuil of SEUILS_ETOILES) if (pourcentage >= seuil) n++
  return n
}

/**
 * Les points d'une partie.
 *
 * Une bonne reponse vaut son niveau (une reussite de 6e annee compte plus
 * qu'une de 1re), le chronometre ajoute la moitie, et une serie parfaite donne
 * un bonus. Rien n'est jamais retire: on ne punit pas un enfant qui joue.
 */
export function pointsPartie(niveau: number, justes: number, total: number, chrono: boolean): number {
  if (total <= 0) return 0
  const base = justes * niveau
  const bonusChrono = chrono ? Math.round(base * 0.5) : 0
  const bonusParfait = justes === total ? niveau * 5 : 0
  return base + bonusChrono + bonusParfait
}
